import PulseBar from "@/components/PulseBar";
import ModeBadge from "@/components/ModeBadge";
import StatTile from "@/components/StatTile";
import SignalCard from "@/components/SignalCard";
import PositionCard from "@/components/PositionCard";
import AiFeed from "@/components/AiFeed";
import PnlHistoryChart from "@/components/PnlHistoryChart";
import LogBox from "@/components/LogBox";
import Tabs from "@/components/Tabs";
import NotificationToasts from "@/components/NotificationToasts";
import RunNowButton from "@/components/RunNowButton";
import SettingsPanel from "@/components/SettingsPanel";
import SymbolManagementPanel from "@/components/SymbolManagementPanel";
import BacktestPanel from "@/components/BacktestPanel";
import ObservabilityPanel from "@/components/ObservabilityPanel";
import MemeSleevePanel from "@/components/MemeSleevePanel";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useDashboardData } from "@/hooks/useDashboardData";
import { closeAllPositions } from "@/api/client";
import { useState } from "react";

const KILL_SWITCH_REASON_LABEL: Record<string, string> = {
  daily_loss: "daily loss limit",
  profit_target: "daily profit target",
  trading_disabled: "trading disabled",
  emergency_stop: "emergency stop",
  max_drawdown: "max drawdown",
  daily_trade_limit: "daily trade limit",
  outside_session_hours: "outside session hours",
  weekday_not_allowed: "weekday not allowed",
};
interface DashboardProps {
  onLogout: () => void;
  onUnauthorized: () => void;
}
export default function Dashboard({ onLogout, onUnauthorized }: DashboardProps) {
  const { overview, signals, positions, decisions, pnlHistory, errors, logs, loading, refresh } =
    useDashboardData(onUnauthorized);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [symbolsOpen, setSymbolsOpen] = useState(false);
  const [backtestOpen, setBacktestOpen] = useState(false);
  const [closeAllConfirmOpen, setCloseAllConfirmOpen] = useState(false);
  const [closeAllBusy, setCloseAllBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const openPositionsCount = positions?.open.length ?? 0;
  const filteredSignals = (signals?.symbolSignals ?? []).filter((signal) => signal.market === "futures");
  const backtestSymbols = Array.from(new Set(filteredSignals.map((s) => s.symbol)));
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };
  const handleCloseAll = async () => {
    setCloseAllBusy(true);
    try {
      await closeAllPositions();
      await refresh();
    } finally {
      setCloseAllBusy(false);
      setCloseAllConfirmOpen(false);
    }
  };
  return (
    <div className="min-h-screen bg-void pb-16">
      <PulseBar
        marketOverview={overview?.marketOverview ?? null}
        latestScanAt={overview?.latestScanAt ?? null}
        killSwitchTriggered={overview?.killSwitch.triggered ?? false}
        systemStatus={overview?.systemStatus ?? null}
      />

      <header className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
        <div>
          <div className="font-mono text-lg font-semibold text-ink">
            <span className="text-turquoise">AI</span> Trader
          </div>
          <div className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-ink-faint">
            {overview?.environment ?? "—"} console
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {overview && <ModeBadge dryRun={overview.dryRun} />}
          <RunNowButton />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-md border border-line px-3 py-1.5 font-mono text-xs text-ink-dim transition hover:border-turquoise/50 hover:text-turquoise disabled:opacity-50"
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {openPositionsCount > 0 && (
            <button
              onClick={() => setCloseAllConfirmOpen(true)}
              className="rounded-md border border-sell-dim/50 px-3 py-1.5 font-mono text-xs font-semibold text-sell transition hover:bg-sell/10"
            >
              Close All ({openPositionsCount})
            </button>
          )}
          <button
            onClick={() => setSymbolsOpen(true)}
            className="rounded-md border border-line px-3 py-1.5 font-mono text-xs text-ink-dim transition hover:border-turquoise/50 hover:text-turquoise"
          >
            Symbols
          </button>
          <button
            onClick={() => setBacktestOpen(true)}
            className="rounded-md border border-line px-3 py-1.5 font-mono text-xs text-ink-dim transition hover:border-turquoise/50 hover:text-turquoise"
          >
            Backtest
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            className="rounded-md border border-line px-3 py-1.5 font-mono text-xs text-ink-dim transition hover:border-turquoise/50 hover:text-turquoise"
          >
            Settings
          </button>
          <button
            onClick={onLogout}
            className="rounded-md border border-line px-3 py-1.5 font-mono text-xs text-ink-dim transition hover:border-sell/50 hover:text-sell"
          >
            Sign out
          </button>
        </div>
      </header>

      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <SymbolManagementPanel open={symbolsOpen} onClose={() => setSymbolsOpen(false)} />
      <BacktestPanel
        open={backtestOpen}
        onClose={() => setBacktestOpen(false)}
        defaultSymbols={backtestSymbols}
      />
      <NotificationToasts killSwitch={overview?.killSwitch ?? null} />
      <ConfirmDialog
        open={closeAllConfirmOpen}
        title={`Close all ${openPositionsCount} open position${openPositionsCount === 1 ? "" : "s"}?`}
        message="This immediately closes every open futures position at market price and switches on the Emergency stop, so the bot does not open new positions. Turn Emergency stop off in Settings to resume trading. This cannot be undone."
        confirmLabel="Close All"
        busy={closeAllBusy}
        onConfirm={handleCloseAll}
        onCancel={() => setCloseAllConfirmOpen(false)}
      />

      <main className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6">
        {}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatTile
            label="Kill Switch"
            value={overview?.killSwitch.triggered ? "Triggered" : "Armed"}
            sublabel={overview?.killSwitch.triggered ? KILL_SWITCH_REASON_LABEL[overview.killSwitch.reason ?? ""] : undefined}
            tone={overview?.killSwitch.triggered ? "sell" : "turquoise"}
          />
          <StatTile
            label="Daily Loss"
            value={overview ? `${overview.killSwitch.dailyLossPercent.toFixed(1)}%` : "—"}
            sublabel={overview ? `limit ${overview.killSwitch.thresholdPercent}%` : undefined}
            tone={overview?.killSwitch.triggered ? "sell" : "default"}
          />
          <StatTile
            label="Drawdown"
            value={overview ? `${overview.killSwitch.drawdownPercent.toFixed(1)}%` : "—"}
            sublabel={
              overview && overview.killSwitch.maxDrawdownPercent > 0
                ? `limit ${overview.killSwitch.maxDrawdownPercent}%`
                : "no limit set"
            }
            tone={overview?.killSwitch.reason === "max_drawdown" ? "sell" : "default"}
          />
          <StatTile
            label="Open Positions"
            value={overview ? `${overview.openPositionsCount} / ${overview.maxConcurrentPositions}` : "—"}
          />
          <StatTile
            label="Remaining Capital"
            value={
              overview
                ? overview.dryRun || overview.remainingCapitalUsd > 0
                  ? `$${overview.remainingCapitalUsd.toFixed(2)}`
                  : "—"
                : "—"
            }
            sublabel="Free to open new positions"
            tone="turquoise"
          />
          <StatTile
            label="Allocated Capital"
            value={
              overview
                ? overview.dryRun || overview.allocatedCapitalUsd > 0
                  ? `$${overview.allocatedCapitalUsd.toFixed(2)}`
                  : "—"
                : "—"
            }
            sublabel={
              !overview?.dryRun && overview?.accountSnapshotError ? overview.accountSnapshotError : "Assigned to the bot"
            }
            tone={!overview?.dryRun && overview?.accountSnapshotError ? "sell" : "default"}
          />
        </section>

        {}
        <section>
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wide text-ink-dim">
            Open Positions{openPositionsCount > 0 ? ` (${openPositionsCount})` : ""}
          </h2>
          {(positions?.open.length ?? 0) === 0 ? (
            <div className="mt-3 rounded-xl border border-line bg-panel p-8 text-center font-mono text-xs text-ink-faint">
              No open positions.
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
              {positions?.open.map((position) => (
                <PositionCard key={position.id} position={position} onActionComplete={refresh} />
              ))}
            </div>
          )}
        </section>

        {}
        <section>
          <Tabs
            defaultTabId="signals"
            tabs={[
              {
                id: "signals",
                label: "Market Signals",
                count: filteredSignals.length,
                content:
                  filteredSignals.length === 0 ? (
                    <div className="rounded-xl border border-line bg-panel p-8 text-center font-mono text-xs text-ink-faint">
                      {loading ? "Loading signals..." : "No signals available for this filter yet."}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {filteredSignals.map((signal) => (
                        <SignalCard key={`${signal.market}-${signal.symbol}`} signal={signal} />
                      ))}
                    </div>
                  ),
              },
              {
                id: "ai-feed",
                label: "AI Decision Feed",
                content: (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                      <AiFeed decisions={decisions} />
                    </div>
                    <div>
                      <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
                        Daily PnL (14d)
                      </h3>
                      <div className="mt-3 rounded-xl border border-line bg-panel p-4 shadow-panel">
                        <PnlHistoryChart history={pnlHistory} />
                      </div>

                      {errors.length > 0 && (
                        <div className="mt-6">
                          <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
                            Recent Technical Errors
                          </h3>
                          <div className="mt-2 space-y-2">
                            {errors.slice(0, 5).map((technicalError) => {
                              const isError = technicalError.level === "error";
                              return (
                                <div
                                  key={technicalError.id}
                                  className={`min-w-0 overflow-hidden rounded-lg border p-2.5 font-mono text-[11px] text-ink-dim ${isError ? "border-sell-dim/50 bg-sell/5" : "border-gold-dim/50 bg-gold/5"}`}
                                >
                                  <div className={`break-words ${isError ? "text-sell" : "text-gold"}`}>{technicalError.source}</div>
                                  <div className="mt-0.5 break-words">{technicalError.message}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                id: "closed",
                label: "Recently Closed",
                count: positions?.closed.length ?? 0,
                content:
                  (positions?.closed.length ?? 0) === 0 ? (
                    <div className="rounded-xl border border-line bg-panel p-8 text-center font-mono text-xs text-ink-faint">
                      No closed positions yet.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      {positions?.closed.slice(0, 6).map((position) => (
                        <PositionCard key={position.id} position={position} />
                      ))}
                    </div>
                  ),
              },
              {
                id: "logs",
                label: "Logs",
                content: <LogBox logs={logs} />,
              },
              {
                id: "observability",
                label: "Observability",
                content: <ObservabilityPanel decisions={decisions} errors={errors} />,
              },
              {
                id: "meme",
                label: "Meme Sleeve",
                content: <MemeSleevePanel onUnauthorized={onUnauthorized} />,
              },
            ]}
          />
        </section>
      </main>
    </div>
  );
}
